import { useState, useEffect } from 'react';
import axios from 'axios';

// Aynı sistem farklı alan adlarından (crm.ritsaai.com, crm.ticiplus.com.tr)
// farklı marka ile açılıyor - sunucu /api/site-settings'te isteğin geldiği alan
// adına göre logo/isim/renkleri döner (bkz. server/utils/brands.js). Burada bu
// renkler index.css'teki :root CSS değişkenlerinin üzerine yazılır.

const FALLBACK = { site_name: 'CRM', site_logo_url: '', site_favicon_url: '' };
const STORAGE_KEY = 'brand_settings_cache';

// index.css'teki varsayılan değerler - marka rengi bunlarla AYNIYSA hiçbir
// değişken ezilmez, böylece varsayılan markanın görünümü birebir korunur
// (türetilmiş koyu tonlar elle seçilmiş değerlerden biraz farklı çıkardı).
const CSS_DEFAULTS = {
  primary_color: '#00f2fe',
  secondary_color: '#8b5cf6',
  tertiary_color: '#6366f1',
  login_accent_color: '#2563eb'
};

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const darken = (hex, amount) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return '#' + rgb.map(c => Math.round(c * (1 - amount)).toString(16).padStart(2, '0')).join('');
};

const lighten = (hex, amount) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return '#' + rgb.map(c => Math.round(c + (255 - c) * amount).toString(16).padStart(2, '0')).join('');
};

function colorVars(field, hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return {};
  const rgbStr = rgb.join(', ');
  const rgba = (a) => `rgba(${rgbStr}, ${a})`;
  if (field === 'primary_color') {
    return {
      '--primary': hex, '--primary-rgb': rgbStr, '--primary-glow': rgba(0.12),
      '--color-accent-primary': hex, '--color-accent-dark': darken(hex, 0.27),
      '--primary-dark': darken(hex, 0.27), '--primary-light': rgba(0.1),
      '--primary-50': rgba(0.05), '--primary-100': rgba(0.1),
      '--primary-500': hex, '--primary-600': darken(hex, 0.27), '--primary-700': darken(hex, 0.45),
      '--info': hex, '--info-light': rgba(0.1),
      '--glow-blue': `0 0 40px ${rgba(0.25)}, 0 0 80px ${rgba(0.1)}`,
      '--glow-blue-subtle': `0 0 20px ${rgba(0.15)}, 0 4px 24px rgba(0, 0, 0, 0.5)`
    };
  }
  if (field === 'secondary_color') {
    return {
      '--secondary': hex, '--secondary-rgb': rgbStr, '--accent': hex,
      '--accent-light': rgba(0.1), '--color-accent-secondary': hex
    };
  }
  if (field === 'tertiary_color') return { '--tertiary': hex, '--tertiary-rgb': rgbStr };
  if (field === 'login_accent_color') {
    const light = lighten(hex, 0.15);
    const lighter = lighten(hex, 0.35);
    return {
      '--login-accent': hex, '--login-accent-rgb': rgbStr,
      '--login-accent-dark': darken(hex, 0.15),
      '--login-accent-light': light, '--login-accent-light-rgb': hexToRgb(light).join(', '),
      '--login-accent-lighter': lighter, '--login-accent-lighter-rgb': hexToRgb(lighter).join(', '),
      '--login-accent-lightest': lighten(hex, 0.55),
      '--login-glow-2': darken(hex, 0.25), '--login-glow-3': darken(hex, 0.1)
    };
  }
  return {};
}

const appliedVars = new Set();

export function applyBrand(settings) {
  if (!settings) return;
  const root = document.documentElement.style;

  appliedVars.forEach(v => root.removeProperty(v));
  appliedVars.clear();
  for (const field of Object.keys(CSS_DEFAULTS)) {
    const hex = (settings[field] || '').toLowerCase();
    if (!hex || hex === CSS_DEFAULTS[field]) continue;
    for (const [name, value] of Object.entries(colorVars(field, hex))) {
      root.setProperty(name, value);
      appliedVars.add(name);
    }
  }

  if (settings.site_name) document.title = settings.site_name;
  if (settings.site_favicon_url) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = settings.site_favicon_url;
  }
  const themeMeta = document.querySelector("meta[name='theme-color']");
  if (themeMeta && settings.primary_color) themeMeta.setAttribute('content', settings.primary_color);
}

// Son görülen marka (localStorage alan adı başına ayrı) açılışta HEMEN
// uygulanır - sunucu cevabını beklerken varsayılan renk/logo "flaşı" olmasın.
let cache = null;
try {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    cache = JSON.parse(saved);
    applyBrand(cache);
  }
} catch (e) { /* localStorage yoksa sunucu cevabını bekle */ }

let listeners = [];
let inflight = null;
let fetched = false;

function notify() {
  listeners.forEach(fn => fn(cache));
}

export async function fetchSiteSettings() {
  if (!inflight) {
    inflight = axios.get('/api/site-settings')
      .then(({ data }) => {
        cache = data;
        fetched = true;
        applyBrand(data);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (e) { /* yoksay */ }
        notify();
        return data;
      })
      .catch(() => cache || FALLBACK)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

// Yönetim panelinden marka kaydedilince bu alan adının markası değiştiyse
// sayfayı yenilemeden uygulamak için.
export function refreshSiteSettings() {
  return fetchSiteSettings();
}

export function useSiteSettings() {
  const [settings, setSettings] = useState(cache || FALLBACK);

  useEffect(() => {
    const handler = (s) => setSettings(s || FALLBACK);
    listeners.push(handler);
    if (cache) setSettings(cache);
    if (!fetched) fetchSiteSettings();
    return () => { listeners = listeners.filter(fn => fn !== handler); };
  }, []);

  return settings;
}
